import React, { FC, useCallback, useMemo } from 'react';
import { BottomSheet } from '$uikit/BottomSheet/BottomSheet';
import Select from 'react-select';
import isEqual from 'lodash/isEqual';

import { useDispatch, useSelector } from 'react-redux';
import { mainActions } from '$store/main';
import { BroadcastFilter } from '$core/Broadcast/BroadcastFilter/BroadcastFilter';
import { IcDropdown16 } from '$assets';
import {
  BroadcastFilterName,
  BroadcastList,
  BroadcastListEnum,
} from '$store/im/interface';
import { Button, DateRangePicker, Form, FormItem } from '$uikit';
import { useDesktop, useTranslator } from '$hooks';
import { LANGUAGE_MAP } from '$shared/constants/languages';
import { LanguageTags } from '$shared/constants';
import {
  imActions,
  imBroadcastAppliedFilterSelector,
  imBroadcastFilterSelector,
  imBroadcastSelector,
  initialStateFilters,
} from '$store/im';
import { normalizeDateRange } from '$utils';
import { batchActions } from '$store';

export const BroadcastFiltersBottomSheet: FC = () => {
  const dispatch = useDispatch();
  const isDesktop = useDesktop();
  const t = useTranslator();
  const { filters, activeFilters, sendTo } = useSelector(imBroadcastSelector);
  const { languages, subscribeDate, registrationDate, sponsorSubscribeDate } = filters;

  const handleClose = useCallback(() => {
    dispatch(mainActions.setPopup(null));
    dispatch(imActions.cancelBroadcastFilters());
  }, [dispatch]);

  const handleApply = useCallback(() => {
    dispatch(mainActions.setPopup(null));
    dispatch(imActions.applyBroadcastFilters());
  }, [dispatch]);

  const handleChoseLanguage = useCallback((options) => {
    dispatch(imActions.setBroadcastFilterLanguage(options.map((o) => o.value)));
  }, []);

  const options: { value: string; label: string }[] = useMemo(() => {
    return Object.entries(LANGUAGE_MAP).map(([key, language]) => {
      return {
        label: `${language} (${key.toUpperCase()})`,
        value: key,
      };
    });
  }, []);

  const excludeDateIntervals = [{ start: new Date(), end: new Date('3000') }];

  const hasUpdates = useMemo(() => {
    return (
      !isEqual(initialStateFilters, filters) || Object.keys(activeFilters).length > 0
    );
  }, [filters, activeFilters]);

  // @ts-ignore
  return (
    <BottomSheet title={t('broadcast_filters_title')} scroll onClose={handleClose}>
      <div className="BroadcastFiltersBottomSheet">
        <BroadcastFilter
          setActiveOnClickContent
          title={t('broadcast_filter_languages_title')}
          filterName={BroadcastFilterName.LANGUAGES}
        >
          <Select
            classNamePrefix="Select"
            isMulti
            value={options.filter((l) => languages.includes(l.value as LanguageTags))}
            onChange={handleChoseLanguage}
            options={options}
            placeholder={t('broadcast_filter_languages_placeholder')}
            components={{
              DropdownIndicator: () => <IcDropdown16 />,
            }}
          />
        </BroadcastFilter>
        <BroadcastFilter
          title={t('broadcast_filter_subscribe_title')}
          filterName={BroadcastFilterName.SUBSCRIBE_DATE}
        >
          <DateRangePicker
            excludeDateIntervals={excludeDateIntervals}
            startDate={subscribeDate[0]}
            endDate={subscribeDate[1]}
            onChange={(value) =>
              dispatch(
                imActions.setBroadcastFilterSubscribeDate(normalizeDateRange(value)),
              )
            }
            isDesktop={false}
            isClearable={true}
          />
        </BroadcastFilter>
        {sendTo.includes(BroadcastListEnum.SPONSORS) && (
          <BroadcastFilter
            title={t('broadcast_filter_sponsor_subscribe_title')}
            filterName={BroadcastFilterName.SPONSOR_SUBSCRIBE_DATE}
          >
            <DateRangePicker
              excludeDateIntervals={excludeDateIntervals}
              startDate={sponsorSubscribeDate[0]}
              endDate={sponsorSubscribeDate[1]}
              onChange={(value) =>
                dispatch(
                  imActions.setBroadcastFilterSponsorSubscribeDate(
                    normalizeDateRange(value),
                  ),
                )
              }
              selectsRange={true}
              isDesktop={false}
              isClearable={true}
            />
          </BroadcastFilter>
        )}
        <BroadcastFilter
          title={t('broadcast_filter_registration_title')}
          filterName={BroadcastFilterName.REGISTRATION_DATE}
        >
          <DateRangePicker
            excludeDateIntervals={excludeDateIntervals}
            startDate={registrationDate[0]}
            endDate={registrationDate[1]}
            onChange={(value) =>
              dispatch(
                imActions.setBroadcastFilterRegistrationDate(normalizeDateRange(value)),
              )
            }
            selectsRange={true}
            isDesktop={false}
            isClearable={true}
          />
        </BroadcastFilter>
        <Form>
          <FormItem>
            <Button onClick={handleApply}>{t('broadcast_filter_apply_button')}</Button>
          </FormItem>
          <FormItem>
            <Button
              disabled={!hasUpdates}
              onClick={() => {
                dispatch(
                  batchActions(
                    imActions.resetBroadcastFilters(),
                    mainActions.setPopup(null),
                  ),
                );
                dispatch(imActions.applyBroadcastFilters());
              }}
              mode="secondary"
            >
              {t('broadcast_filter_reset_button')}
            </Button>
          </FormItem>
        </Form>
      </div>
    </BottomSheet>
  );
};
